/**
 * Frontend visibility only. Defaults are route patterns in this file.
 * /me sends sparse overlays: user.access = { roleId, role, staff }.
 * Admin writes baker/{id}/role/{roleId}.access and staff.access in Firestore.
 */

const VIEW = { view: true };
const VIEW_EDIT = { view: true, edit: true };
const DENY = { view: false };

// Kitchen views the Orders list (Calendar day click lands on /order?search=date&from=&to=,
// and /dashboard renders the same screen). /order/* also matches create and drafts —
// deny those so they do not show in the drawer or via URL.
const KITCHEN_ORDER_ACTIONS = {
  "/order/create": DENY,
  "/order/draft": DENY,
};

const OWNER_DEFAULTS = {
  "/create": VIEW,
  "/quick-charge": VIEW,
  "/order/create": VIEW,
  "/order/*": VIEW_EDIT,
  "/quote/*": VIEW_EDIT,
  "/catalog/*": VIEW_EDIT,
  "/shop/*": VIEW_EDIT,
  "/inventory/*": VIEW,
  "/calendar": VIEW,
  "/planning/*": VIEW,
  "/planner/*": VIEW,
  "/events/*": VIEW,
  "/reviews": VIEW,
  "/cash-advance": VIEW,
  "/settings/staff": VIEW,
  "/settings/store-location": VIEW,
  "/settings/photos": VIEW,
  "/photo/*": VIEW,
  "/plan": VIEW,
  "/report/*": VIEW,
  "/payout/*": VIEW,
};

const MANAGER_DEFAULTS = {
  "/create": VIEW,
  "/quick-charge": VIEW,
  "/order/create": VIEW,
  "/order/*": VIEW_EDIT,
  "/quote/*": VIEW_EDIT,
  "/catalog/*": VIEW_EDIT,
  "/shop/*": VIEW_EDIT,
  "/inventory/*": VIEW,
  "/calendar": VIEW,
  "/planning/*": VIEW,
  "/planner/*": VIEW,
  "/events/*": VIEW,
  "/reviews": VIEW,
  "/settings/staff": VIEW,
  "/settings/store-location": VIEW,
  "/settings/photos": VIEW,
  "/photo/*": VIEW,
};

const CLERK_DEFAULTS = {
  "/create": VIEW,
  "/quick-charge": VIEW,
  "/order/create": VIEW,
  "/order/*": VIEW,
  // Clerks can open a quote from search, a customer, or an order.
  // /quote/* also matches the Quotes list — deny that inbox.
  "/quote": DENY,
  "/quote/*": VIEW,
};

const KITCHEN_DEFAULTS = {
  "/calendar": VIEW,
  "/planner/*": VIEW,
  ...KITCHEN_ORDER_ACTIONS,
  "/order/*": VIEW,
};

const KITCHEN_LEADER_DEFAULTS = {
  "/calendar": VIEW,
  "/planner/*": VIEW,
  ...KITCHEN_ORDER_ACTIONS,
  "/order/*": VIEW_EDIT,
};

export const ROLE_DEFAULTS = {
  owner: OWNER_DEFAULTS,
  baker: OWNER_DEFAULTS,
  manager: MANAGER_DEFAULTS,
  rep: MANAGER_DEFAULTS,
  clerk: CLERK_DEFAULTS,
  kitchen: KITCHEN_DEFAULTS,
  "kitchen leader": KITCHEN_DEFAULTS,
  kitchen_leader: KITCHEN_LEADER_DEFAULTS,
};

const GATED_PREFIXES = [
  "/create",
  "/quick-charge",
  "/order",
  "/quote",
  "/catalog",
  "/shop",
  "/inventory",
  "/calendar",
  "/planning",
  "/planner",
  "/events",
  "/reviews",
  "/cash-advance",
  "/loan",
  "/settings",
  "/photo",
  "/plan",
  "/report",
  "/payout",
];

const isAccessMap = (value) => Boolean(value && typeof value === "object" && !Array.isArray(value));

export function normalizePathname(pathname = "") {
  const path = pathname.split("?")[0] || "/";
  if (path === "/my/order" || path.startsWith("/my/order/")) {
    return path.replace(/^\/my\/order/, "/order");
  }
  if (path === "/my/quote" || path.startsWith("/my/quote/")) {
    return path.replace(/^\/my\/quote/, "/quote");
  }
  if (path === "/loan" || path.startsWith("/loan/")) {
    return path.replace(/^\/loan/, "/cash-advance");
  }
  return path;
}

function patternMatches(pattern, pathname) {
  if (pattern.endsWith("/*")) {
    const prefix = pattern.slice(0, -2);
    return pathname === prefix || pathname.startsWith(`${prefix}/`);
  }
  return pathname === pattern;
}

function specificity(pattern) {
  if (pattern.endsWith("/*")) {
    return pattern.slice(0, -2).length;
  }
  return pattern.length + 1000;
}

function mergeMaps(...maps) {
  const next = {};
  maps.forEach((map) => {
    if (!isAccessMap(map)) {
      return;
    }
    Object.entries(map).forEach(([pattern, node]) => {
      if (!isAccessMap(node)) {
        return;
      }
      next[pattern] = { ...(next[pattern] || {}), ...node };
    });
  });
  return next;
}

function isGatedPath(pathname) {
  return GATED_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

function resolveFlags(user, pathname) {
  // old /me has no access object — stay open so a functions-first deploy cannot lock owners out
  if (!user?.access || typeof user.access !== "object" || Array.isArray(user.access)) {
    return { view: true, edit: true };
  }

  const path = normalizePathname(pathname);
  const roleId = user.access.roleId || user.role;
  const defaults = ROLE_DEFAULTS[roleId] || {};
  const patterns = mergeMaps(defaults, user.access.role, user.access.staff);

  let match;
  Object.keys(patterns).forEach((pattern) => {
    if (!patternMatches(pattern, path)) {
      return;
    }
    const score = specificity(pattern);
    if (!match || score > match.score) {
      match = { score, node: patterns[pattern] };
    }
  });

  if (!match) {
    return isGatedPath(path) ? { view: false, edit: false } : { view: true };
  }

  return {
    view: match.node.view === true,
    edit: match.node.edit === true,
  };
}

export function canAccessPath(user, pathname) {
  return resolveFlags(user, pathname).view === true;
}

export function canEditPath(user, pathname) {
  const flags = resolveFlags(user, pathname);
  return flags.view === true && flags.edit === true;
}
